import React, { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, ChevronDown } from "lucide-react";
import { Reveal, Overline } from "@/components/ui-bits";
import { submitLeadMagnet, NEED_OPTIONS, CHECKLIST_PDF_URL } from "@/lib/api";

const GUIDE_FILENAME = "BATEar-Getting-Started-in-Ghana.pdf";

async function downloadGuide() {
  const res = await fetch(CHECKLIST_PDF_URL);
  if (!res.ok) throw new Error("PDF unavailable");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = GUIDE_FILENAME;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function LeadMagnet() {
  const [form, setForm] = useState({ name: "", email: "", need: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.need) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      await submitLeadMagnet(form);
      try {
        await downloadGuide();
        toast.success(
          "Your guide is downloading — a copy is also on its way to your inbox."
        );
      } catch {
        toast.success("Your guide is on its way to your inbox.");
      }
      setForm({ name: "", email: "", need: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="lead-magnet"
      className="relative bg-navy grain py-24 sm:py-32"
      data-testid="lead-magnet-section"
    >
      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
        <div className="border border-gold/30 bg-navy-deep/60 p-8 sm:p-14">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Overline>Free Download</Overline>
              <h2 className="mt-5 font-display font-bold text-white leading-[1.08] tracking-tight text-3xl sm:text-4xl">
                Getting Started in Ghana
              </h2>
              <p className="mt-5 text-white/70 leading-relaxed">
                Before you commit to anything in Ghana, download our free
                guide — a practical overview of how to plan, verify, and get
                properly established on the ground.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-6" data-testid="lead-magnet-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-gold">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="lead-name-input"
                  placeholder="Your full name"
                  className="w-full bg-transparent border-b border-white/25 focus:border-gold text-white rounded-none px-0 py-3 outline-none transition-colors placeholder:text-white/35"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-gold">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="lead-email-input"
                  placeholder="you@email.com"
                  className="w-full bg-transparent border-b border-white/25 focus:border-gold text-white rounded-none px-0 py-3 outline-none transition-colors placeholder:text-white/35"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-gold">
                  What do you need help with?
                </label>
                <div className="relative">
                  <select
                    value={form.need}
                    onChange={(e) => setForm({ ...form, need: e.target.value })}
                    data-testid="lead-need-select"
                    className={`w-full bg-transparent border-b border-white/25 focus:border-gold rounded-none px-0 py-3 mt-1 outline-none transition-colors appearance-none cursor-pointer pr-8 ${
                      form.need ? "text-white" : "text-white/35"
                    }`}
                  >
                    <option value="" disabled className="text-navy-deep">
                      Select an option
                    </option>
                    {NEED_OPTIONS.map((o) => (
                      <option key={o} value={o} className="text-navy-deep">
                        {o}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={18}
                    className="text-gold absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="lead-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy-deep hover:bg-gold-light transition-colors duration-300 font-semibold px-6 py-4 text-sm uppercase tracking-wider disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Download size={18} />
                )}
                Send Me the Guide
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
